/**
 * Small pure helpers for dashboards: heatmap bucketing, SLA ageing, shares.
 */

/** Tailwind classes per heat level (token-backed palettes; literal so JIT keeps them). */
export const HEAT_CLASSES = ['bg-success-100', 'bg-success-300', 'bg-warning-200', 'bg-warning-400', 'bg-error-400', 'bg-error-600 text-white'];

/** Bucket `value` into 0..levels-1 between min and max (inclusive). */
export function heatLevel(value, min, max, levels = HEAT_CLASSES.length) {
  if (value == null || Number.isNaN(Number(value))) return 0;
  if (max <= min) return 0;
  const t = (Number(value) - min) / (max - min);
  return Math.max(0, Math.min(levels - 1, Math.floor(t * levels)));
}

/** Min and max over a 2-D grid of numbers. */
export function gridRange(rows, cols, get) {
  let min = Infinity;
  let max = -Infinity;
  for (const r of rows) for (const c of cols) {
    const v = get(r, c);
    if (v == null) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
}

/** Hours between two ISO timestamps (b defaults to `now`). */
export function hoursBetween(a, b) {
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}

/** SLA state for an item due at `dueAt`: breached, due_soon (<= warnHours), on_track. */
export function slaState(dueAt, now, warnHours = 4) {
  const left = hoursBetween(now, dueAt);
  if (left < 0) return 'breached';
  if (left <= warnHours) return 'due_soon';
  return 'on_track';
}

/** Human "3h 20m" / "2d 4h" for a positive or negative number of hours. */
export function formatHours(h) {
  const sign = h < 0 ? '-' : '';
  const abs = Math.abs(h);
  if (abs >= 48) return `${sign}${Math.floor(abs / 24)}d ${Math.round(abs % 24)}h`;
  const hours = Math.floor(abs);
  return `${sign}${hours}h ${Math.round((abs - hours) * 60)}m`;
}

/** { key: n } → [{ name, value, pct }] sorted by value desc. */
export function shares(map) {
  const total = Object.values(map || {}).reduce((a, b) => a + b, 0);
  return Object.entries(map || {})
    .map(([name, value]) => ({ name, value, pct: total ? Math.round((value / total) * 100) : 0 }))
    .sort((a, b) => b.value - a.value);
}

/** Funnel step conversion: [{ step, count }] → adds pctOfTop and pctOfPrev. */
export function funnel(steps) {
  const top = steps.length ? steps[0].count : 0;
  return steps.map((s, i) => ({
    ...s,
    pctOfTop: top ? Math.round((s.count / top) * 1000) / 10 : 0,
    pctOfPrev: i === 0 ? 100 : steps[i - 1].count ? Math.round((s.count / steps[i - 1].count) * 1000) / 10 : 0,
  }));
}
